import { BankConnection } from '@core/model/domain/bankConnection';
import type { Logger } from '@core/model/domain/logger';
import { poll } from '@core/model/framework/notifications/polling';
import { Bad } from '@core/model/framework/Result/BadResult';
import { Ok } from '@core/model/framework/Result/OkResult';
import { WaitOnWebauthNotificationGateway } from '@core/model/gateway/waitOnWebauthNotificationGateway';
import type { BankService } from '@core/model/service/bankService';
import {
  WaitOnWebauthNotificationInput,
  WaitOnWebauthNotificationOutput,
} from '@core/usecase/waitOnWebauthNotification';
import { SERVICE_ID } from '@di/serviceId';
import { inject, injectable } from 'inversify';
import { BehaviorSubject, Observable, TimeoutError, firstValueFrom, from } from 'rxjs';
import { assert } from 'ts-essentials';

@injectable()
export class WaitOnWebauthNotificationViaPolling implements WaitOnWebauthNotificationGateway {
  private existingBankConnections!: BankConnection[];
  private manualForceStopPolling$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    @inject(SERVICE_ID.bankService)
    private readonly bankService: BankService,
    @inject(SERVICE_ID.waitOnWebauthTimeoutInSeconds)
    private readonly timeoutInSeconds: number,
    @inject(SERVICE_ID.waitOnWebauthIntervalInSeconds)
    private readonly intervalInSeconds: number,
    @inject(SERVICE_ID.logger)
    private readonly logger: Logger
  ) {}

  async awaitNewBankConnection(input: WaitOnWebauthNotificationInput): WaitOnWebauthNotificationOutput {
    // TODO: should not retain a sort of state here
    this.manualForceStopPolling$.next(false);
    const hasBankConnectionsBeenStored = (await this.storeExistingBankConnections(input)) === true;

    if (!hasBankConnectionsBeenStored) {
      return Bad('FAILED_TO_STORE_EXISTING_BANK_CONNECTIONS');
    } else {
      return this.getNewBankConnectionViaPolling(input);
    }
  }

  stopWaitingNewBankConnection(): void {
    this.manualForceStopPolling$.next(true);
  }

  private async storeExistingBankConnections(input: WaitOnWebauthNotificationInput): Promise<boolean> {
    try {
      const bankConnections = await this.bankService.getBankConnections(input);
      this.existingBankConnections = bankConnections.value;
      return true;
    } catch (_e) {
      return false;
    }
  }

  private async getNewBankConnectionViaPolling(
    input: WaitOnWebauthNotificationInput
  ): Promise<WaitOnWebauthNotificationOutput> {
    try {
      const bankConnections = await firstValueFrom(
        poll<WaitOnWebauthNotificationInput, BankConnection[]>({
          intervalInSeconds: this.intervalInSeconds,
          timeoutInSeconds: this.timeoutInSeconds,
          apiCall: () => this.getNewBankConnectionsAsObservable(input),
          shouldContinuePolling: (updatedBankConnections) =>
            this.shouldContinuePolling(this.existingBankConnections, updatedBankConnections),
          manualForceStop: this.manualForceStopPolling$,
          logger: this.logger,
          pollingName: 'NEW_BANK_CONNECTION',
        })
      );

      const newBankConnection = this.findNewBankConnection(this.existingBankConnections, bankConnections);
      assert(newBankConnection != null, 'should be defined to exit from polling');
      return Ok(newBankConnection);
    } catch (e) {
      if (e instanceof TimeoutError) {
        return Bad('WAITED_NEW_BANK_CONNECTION_FOR_TOO_LONG');
      } else if (e === 'STOPPED_POLLING_VIA_USER_ACTION') {
        return Bad('STOPPED_POLLING_VIA_USER_ACTION');
      }

      this.logger.logException(e);
      throw e;
    }
  }

  private getNewBankConnectionsAsObservable = (
    input: WaitOnWebauthNotificationInput
  ): Observable<BankConnection[]> => {
    return new Observable((observer) => {
      from(
        this.bankService.getBankConnections({
          userId: input.userId,
          userToken: input.userToken,
        })
      ).subscribe({
        next: (result) => {
          if (result.success === true) {
            observer.next(result.value);
          }
          // TODO : handle Ok / Bad
          observer.next([]);
        },
      });
    });
  };

  private shouldContinuePolling = (
    existingBankConnections: BankConnection[],
    updatedBankConnections: BankConnection[]
  ): boolean => {
    const newBankConnection = this.findNewBankConnection(existingBankConnections, updatedBankConnections);

    if (newBankConnection) {
      return false;
    }
    return true;
  };

  private findNewBankConnection(
    existingBankConnections: BankConnection[],
    updatedBankConnections: BankConnection[]
  ): BankConnection | undefined {
    const existingConnectionsId = existingBankConnections.map((c) => c.connectionId);

    if (existingBankConnections.length === updatedBankConnections.length) {
      return undefined;
    } else {
      const newBankConnection = updatedBankConnections.find((updatedConnection) => {
        if (!existingConnectionsId.includes(updatedConnection.connectionId)) {
          return true;
        } else {
          return false;
        }
      });

      return newBankConnection;
    }
  }
}
